import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames/bind';
import { useHistory } from 'react-router-dom';
import InputText from 'app/components/common/InputText/InputText';
import { InputText as InputTextV2, Button } from '@encarpkg/design';
import { getCodeInvalidMessage, getPhoneInvalidMessage } from 'app/libs/inputValidation';
import { testMobile, testNumber } from 'app/libs/regEx';
import { checkDuplicatePhoneAndName } from 'app/api';
import LoadingBar from 'app/components/common/LoadingBar';
import useGoLogin from 'app/hook/common/useGoLogin';
import dayjs from 'app/libs/dayjs';
import useJoinPhoneCertification from 'app/hook/join/useJoinPhoneCertification';
import mobileAppBridge from 'app/libs/MobileAppBridge';
import useIsMounted from 'app/hook/common/useIsMounted';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';
import { getSessionItem } from 'app/libs/storageUtils';
import { LOGIN_RETURN_URL_KEY } from 'app/constants/login';
import useDuplicateSns from 'app/hook/join/useDuplicateSns';
import useIsomorphicLayoutEffect from 'app/hook/common/useIsomorphicLayoutEffect';
import DuplicateJoinConfirmModal from './DuplicateJoinConfirmModal';
import styles from './JoinPhoneCertificationForm.module.scss';

const cx = classNames.bind(styles);

function JoinPhoneCertificationForm({
	isSns = false,
	isV2 = false,
	onSuccess,
	isRequired = false,
	defaultPhone = undefined,
	defaultVerifiedPhone = undefined,
	bindValidationChecker = undefined,
	phoneRef = undefined,
	phoneDescriptionText = undefined,
	duplicateCheckUserName = undefined,
	beforeApplyCode = () => {},
}) {
	const history = useHistory();
	const ua = useSsrUserAgent();
	const { isApp, isAndroid } = ua();
	const goLogin = useGoLogin();
	const [loading, setLoading] = useState(false);
	const [phone, setPhone] = useState(defaultVerifiedPhone || defaultPhone || '');
	const [code, setCode] = useState('');
	const [phoneInvalidMessage, setPhoneInvalidMessage] = useState('');
	const [codeInvalidMessage, setCodeInvalidMessage] = useState('');
	const codeRef = useRef(null);
	const [verifiedPhone, setVerifiedPhone] = useState(defaultVerifiedPhone);
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
	const isMounted = useIsMounted();
	const {
		applyPhoneCertification,
		verifyPhoneCertification,
		certificationCodeExpireTime,
		certificationCodeRemainTime,
		isBlockedUser,
		isCertificationExpired,
	} = useJoinPhoneCertification();
	const { getSnsDuplicateList, isInit } = useDuplicateSns();

	useEffect(() => {
		if (isCertificationExpired) {
			setCodeInvalidMessage('인증번호 입력시간을 초과하였습니다.');
		}
	}, [isCertificationExpired]);

	// 인증번호 입력 필드가 렌더링된 후 focus 실행
	useIsomorphicLayoutEffect(() => {
		if (certificationCodeExpireTime && codeRef.current) {
			codeRef.current.focus();
		}
	}, [certificationCodeExpireTime]);

	const validForm = useCallback(() => {
		const invalidPhone = getPhoneInvalidMessage(phone);
		const invalidCode = !verifiedPhone && getCodeInvalidMessage(code);

		setPhoneInvalidMessage(invalidPhone);
		setCodeInvalidMessage(invalidCode);

		if (invalidPhone) {
			return false;
		}
		if (!verifiedPhone) {
			setCodeInvalidMessage('휴대폰번호 인증을 확인해주세요.');
			return false;
		}

		return true;
	}, [code, phone, verifiedPhone]);

	useEffect(() => {
		if (bindValidationChecker) {
			// eslint-disable-next-line
			bindValidationChecker.current = validForm;
		}
	}, [bindValidationChecker, validForm]);

	const handlePhone = (e) => {
		const { value } = e.target;
		if (value.length < 14 && (value === '' || testNumber(value))) {
			setPhone(e.target.value);
		}
	};
	const handleCode = (e) => setCode(e.target.value);

	const handlePhoneValidation = (e) => {
		setPhoneInvalidMessage(getPhoneInvalidMessage(e.target.value));
	};

	const verifyCode = async () => {
		if (loading || verifiedPhone) return;
		const invalidCodeMessage = getCodeInvalidMessage(code);
		setCodeInvalidMessage(invalidCodeMessage);
		if (invalidCodeMessage) {
			if (codeRef.current) {
				codeRef.current.focus();
			}
			return;
		}

		if (isCertificationExpired) {
			setCodeInvalidMessage('인증번호 입력시간을 초과하였습니다.');
			return;
		}

		try {
			setLoading(true);
			const result = await verifyPhoneCertification(code);
			setPhone(result.phone);
			setVerifiedPhone(result.phone);
			setCodeInvalidMessage('');
			onSuccess(result.phone, result.key);
			alert('인증되었습니다.');
		} catch (e) {
			if (e.response && e.response.status === 400) {
				setCodeInvalidMessage('인증번호가 일치하지 않습니다.');
				if (codeRef.current) {
					codeRef.current.focus();
				}
			} else {
				console.error(e);
				alert('문제가 발생했습니다.');
			}
		} finally {
			setLoading(false);
		}
	};

	const disabledApplyPhone = useMemo(() => !phone || !testMobile(phone) || isBlockedUser, [phone, isBlockedUser]);

	const requestCertificationCode = useCallback(async () => {
		if (certificationCodeExpireTime) {
			const calcTime = dayjs().add(2, 'minutes').add(55, 'second');
			if (certificationCodeExpireTime.isAfter(calcTime)) {
				alert('이미 요청되었습니다. 잠시 후 다시 시도해주세요.');
				setLoading(false);
				return;
			}
		}

		try {
			if (isApp && isAndroid) {
				mobileAppBridge().openSmsRetriever();
				window.onReceivedOtp = (receivedCode) => {
					if (isMounted() && !verifiedPhone) {
						setCode((prev) => prev || receivedCode);
					}
				};
			}
			await applyPhoneCertification(phone);
			setCodeInvalidMessage('');
		} catch (e) {
			if (e.response) {
				alert('인증번호 발송 과정에서 오류가 발생했습니다. 잠시 후 다시 시도해주세요');
			} else {
				alert('문제가 발생했습니다.');
			}
		} finally {
			setLoading(false);
		}
	}, [applyPhoneCertification, certificationCodeExpireTime, isAndroid, isApp, isMounted, phone, verifiedPhone]);

	const applyCode = useCallback(async () => {
		if (verifiedPhone || disabledApplyPhone) return;

		setLoading(true);

		if (!certificationCodeExpireTime && duplicateCheckUserName && !isSns) {
			try {
				await checkDuplicatePhoneAndName(duplicateCheckUserName, phone);
				setLoading(false);
				setIsDuplicateModalOpen(true);
				return;
			} catch (e) {
				if (e.response && e.response.status === 404) {
					console.log('기존 가입이력 없음, 계속 진행');
				} else {
					console.error(e);
				}
			}
		}

		await requestCertificationCode();
	}, [certificationCodeExpireTime, disabledApplyPhone, duplicateCheckUserName, isSns, phone, requestCertificationCode, verifiedPhone]);

	// 기존 가입 이력 모달 - 취소 시 로그인 화면으로 이동
	const handleDuplicateModalCancel = () => {
		setIsDuplicateModalOpen(false);
		const loginReturnUrl = getSessionItem(LOGIN_RETURN_URL_KEY);
		goLogin({ returnUrl: loginReturnUrl });
	};

	// 기존 가입 이력 모달 - 새로 가입 시 인증번호 요청 진행
	const handleDuplicateModalConfirm = () => {
		setIsDuplicateModalOpen(false);
		setLoading(true);
		requestCertificationCode();
	};

	const duplicateModal = (
		<DuplicateJoinConfirmModal
			isOpen={isDuplicateModalOpen}
			onCancel={handleDuplicateModalCancel}
			onConfirm={handleDuplicateModalConfirm}
		/>
	);

	const checkDuplicateSnsId = async () => {
		if (!certificationCodeExpireTime && duplicateCheckUserName && !isInit) {
			const { data } = await getSnsDuplicateList(duplicateCheckUserName, phone);

			if (!data) {
				if (beforeApplyCode) beforeApplyCode(phone);
				applyCode();
				return;
			}
			if (data.signedSites.length > 0) {
				if (beforeApplyCode) beforeApplyCode(phone);
				history.push('/join/sns/duplicate');
			}
		} else {
			applyCode();
		}
	};

	const btnDom = !verifiedPhone ? (
		<Button.V2
			domType="button"
			color="primarylight"
			size={30}
			className={cx('btn')}
			disabled={disabledApplyPhone}
			onClick={isV2 ? applyCode : checkDuplicateSnsId}
		>
			{certificationCodeExpireTime ? '재요청' : '인증번호 요청'}
		</Button.V2>
	) : null;

	if (isSns || isV2) {
		return (
			<>
				<InputTextV2
					label="휴대전화번호"
					useLabel
					type="tel"
					id="cellphone"
					name="cellphone"
					value={phone}
					placeholder="‘-’ 제외하고 숫자만 입력"
					onChange={handlePhone}
					onBlur={handlePhoneValidation}
					validation={!!phoneInvalidMessage}
					validationText={phoneInvalidMessage}
					ref={phoneRef}
					readOnly={!!verifiedPhone}
					required={isRequired}
					addDom={btnDom}
					labelStyle={isV2 ? { marginTop: '20px' } : {}}
				/>

				{(certificationCodeExpireTime || isV2) && (
					<div className={cx('code_area')}>
						<InputTextV2
							useLabel={false}
							label="인증번호"
							inputMode="numeric"
							id="code"
							name="code"
							value={code}
							placeholder="인증번호 입력"
							onChange={handleCode}
							validation={!!codeInvalidMessage}
							validationText={codeInvalidMessage}
							readOnly={!!verifiedPhone || disabledApplyPhone}
							ref={codeRef}
							addDom={
								<>
									{certificationCodeRemainTime && (
										<span className={cx('remain')}>
											{parseInt(certificationCodeRemainTime.format('x'), 10) > 0 &&
												`${certificationCodeRemainTime.format('mm:ss')}`}
										</span>
									)}
									{!!certificationCodeExpireTime && (
										<Button.V2
											domType="button"
											color="primarylight"
											size={30}
											className={cx('btn')}
											onClick={verifyCode}
										>
											인증하기
										</Button.V2>
									)}
								</>
							}
						/>
					</div>
				)}

				{duplicateModal}
			</>
		);
	}

	return (
		<>
			<InputText
				label="휴대전화번호"
				type="tel"
				id="cellphone"
				name="cellphone"
				value={phone}
				placeholder="‘-’ 제외하고 숫자만 입력"
				onChange={handlePhone}
				onBlur={handlePhoneValidation}
				validation={!!phoneInvalidMessage}
				validationText={phoneInvalidMessage}
				ref={phoneRef}
				readOnly={!!verifiedPhone}
				useLabel
				required={isRequired}
				useBtn={!verifiedPhone}
				btnDisabled={disabledApplyPhone}
				btnName={certificationCodeExpireTime ? '인증번호 재요청' : '인증번호 요청'}
				onClick={applyCode}
				style={{ marginTop: '30px' }}
			/>
			{phoneDescriptionText && (
				<p
					className="fs12 fcBlack3"
					style={{ marginTop: '10px' }}
				>
					{phoneDescriptionText}
				</p>
			)}
			{!verifiedPhone && (
				<InputText
					useLabel
					label="인증번호"
					inputMode="numeric"
					id="code"
					name="code"
					value={code}
					placeholder="인증번호 입력"
					onChange={handleCode}
					validation={!!codeInvalidMessage}
					validationText={codeInvalidMessage}
					useBtn={!!certificationCodeExpireTime}
					btnName="확인"
					readOnly={!!verifiedPhone || disabledApplyPhone}
					onClick={verifyCode}
					style={{ marginTop: '30px' }}
					ref={codeRef}
				/>
			)}
			{certificationCodeRemainTime && (
				<p
					className="fcMain fs13"
					style={{ marginTop: 10 }}
				>
					{parseInt(certificationCodeRemainTime.format('x'), 10) > 0 && `남은 시간 : ${certificationCodeRemainTime.format('mm:ss')}`}
				</p>
			)}

			{duplicateModal}

			{loading && <LoadingBar />}
		</>
	);
}

export default JoinPhoneCertificationForm;
